import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { clearStartJourney, markImportFinished, peekImportFinished, persistStartJourney, readStartJourney } from "./start-flags";
import { buildPreviewFromMapping, mappingIsUsable, parseSpreadsheetFile } from "./parse-spreadsheet";
import { SAMPLE_FILE_NAME, getSampleExtraction, getSampleParsedSpreadsheet } from "./sample-operation";
import { sampleProcessScript, shouldSkipProcessingDelay, uploadProcessScript, wait } from "./processing-script";
import {
  extractionFromParsedTable,
  extractionFromMapping,
  extractionToPreview,
  normalizeExtraction,
  applyOperatorSchool,
  patchPassenger,
  removePassengers,
  restorePassengers,
} from "./import/extraction-to-preview";
import { analyzeSpreadsheet, matchPassengers, startCommitJob, waitForCommitJob } from "./import/start-import-api";
import { createDemoSession } from "./demo-session";
import { markEnterStartExperience } from "./start-flags";
import { clampCapacity, draftServicePlan, PLACEHOLDER_SCHOOL_NAME, GENERIC_SERVICE_LABEL } from "./import/cluster-preview";
import {
  EXISTING_MODES,
  INTENTS,
  SERVICE_DIRECTIONS,
  intentCreatesRoutes,
  stagesForJourney,
  suggestIntent,
  suggestServiceDirection,
} from "./import/intent";
import { logStart } from "./start-log";

export const START_STAGES = ["welcome", "understand", "prepare", "visualize", "apps", "continue"];
const MIN_PROCESS_MS = 2200;
const ANALYZE_WAIT_LOCAL_MS = 8000;
const ANALYZE_WAIT_EMPTY_MS = 20000;
const MATCH_DEBOUNCE_MS = 500;
const DEFAULT_MAX_PASSENGERS = 15;

const isTest = () => process.env.NODE_ENV === "test";

// sendRequest has no timeout; when the network is down a button would spin forever. Requests that
// must answer quickly (open a workspace, start the job) race a deadline so the user gets the button
// back with a reason. The job itself is watched by polling and has its own stall detection.
export const WORKSPACE_TIMEOUT_MS = 30000;
export const withDeadline = (promise, ms, message) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(Object.assign(new Error(message), { code: "TIMEOUT" })), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });

// Live view of a running import: what the server reported plus every row it has shown us so far
// (the server only sends the tail of the feed; the client keeps the whole list for the table).
export const EMPTY_PROGRESS = Object.freeze({ jobId: null, phase: "queued", done: 0, total: 0, counts: {}, rows: [] });

export const mergeJobProgress = (prev, job, jobId) => {
  const base = prev && prev.jobId === jobId ? prev : { ...EMPTY_PROGRESS, jobId };
  const seen = new Set(base.rows.map((row) => String(row.rowNumber)));
  const added = (job?.recent || []).filter((row) => row && !seen.has(String(row.rowNumber)));
  return {
    jobId,
    phase: job?.phase || base.phase,
    done: Number.isFinite(job?.done) ? job.done : base.done,
    total: Number.isFinite(job?.total) && job.total > 0 ? job.total : base.total,
    counts: { ...base.counts, ...(job?.counts || {}) },
    rows: added.length ? [...base.rows, ...added] : base.rows,
  };
};

// A job can fail after some rows were written (e.g. a route limit hit after saving). Say so, so
// nobody retries blindly and doubles the roster.
export const describeCommitError = (err) => {
  // A lost job's server text ("no longer available") sounds like the sample itself is missing.
  // The client message says the save can be started again.
  const base =
    err?.code === "JOB_LOST"
      ? err?.message || "This save didn't finish. Start it again — it does not continue from the last attempt."
      : err?.payload?.message || err?.message || "The operation could not be created.";
  const saved = err?.payload?.persist?.studentCount || 0;
  if (saved > 0 && err?.code !== "CONFIRM_EXISTING") {
    return `${base} ${saved} ${saved === 1 ? "student was" : "students were"} already saved before this happened — check the list before saving again.`;
  }
  return base;
};

const restoreJourney = () => {
  const saved = readStartJourney();
  if (!saved) return null;
  const common = {
    commitJobId: saved.commitJobId || null,
    commitSession: saved.commitSession?.token ? { id: saved.commitSession.id, token: saved.commitSession.token } : null,
    schoolName: saved.schoolName || "",
    schoolAddress: saved.schoolAddress || "",
    schoolDecision: saved.schoolDecision || "",
    maxPassengers: clampCapacity(saved.maxPassengers, DEFAULT_MAX_PASSENGERS),
    preferBundled: Boolean(saved.preferBundled),
    selectedRouteId: saved.selectedRouteId || null,
    walkthroughRole: saved.walkthroughRole || "dispatcher",
    intent: saved.intent || null,
    intentTouched: Boolean(saved.intentTouched),
    existingMode: saved.existingMode || EXISTING_MODES.SKIP,
    existingDecisions: saved.existingDecisions || {},
    includeRouteList: Boolean(saved.includeRouteList),
    serviceDirection: saved.serviceDirection || null,
    serviceDirectionTouched: Boolean(saved.serviceDirectionTouched),
    commitResult: saved.commitResult || null,
    commitError: saved.commitError || null,
    error: null,
  };
  if (saved.source === "sample") {
    // The sample is deterministic: rebuild it instead of storing 85 rows.
    const parsed = getSampleParsedSpreadsheet();
    const extraction = normalizeExtraction(getSampleExtraction(), { interpreter: "sample" });
    return {
      ...common,
      intent: saved.intent || INTENTS.PASSENGERS_ROUTES,
      intentTouched: true,
      stage: "continue",
      source: "sample",
      fileName: SAMPLE_FILE_NAME,
      parsed,
      mapping: parsed.mapping,
      preview: extractionToPreview(extraction),
      extraction,
      interpreter: "sample",
      schoolDecision: "found",
      schoolName: extraction.schools[0]?.name || "",
      schoolAddress: extraction.schools[0]?.address || "",
      // A sample that already failed has nothing to resume. Drop the error so it can start again.
      commitError: saved.commitJobId || saved.commitSession?.token ? saved.commitError || null : null,
    };
  }
  if (saved.source === "upload" && saved.parsed?.rows && saved.parsed?.headers) {
    const mapping = saved.mapping || saved.parsed.mapping;
    const extraction = saved.extraction
      ? normalizeExtraction(saved.extraction, {
          interpreter: saved.interpreter || saved.extraction.interpreter,
          keepEmpty: true,
          routeList: saved.extraction.routes || saved.parsed.routeList || [],
        })
      : extractionFromParsedTable({ ...saved.parsed, mapping });
    return {
      ...common,
      stage: saved.stage && START_STAGES.includes(saved.stage) ? saved.stage : "understand",
      source: "upload",
      fileName: saved.fileName,
      parsed: { ...saved.parsed, mapping },
      mapping,
      preview: extractionToPreview(extraction),
      extraction,
      interpreter: extraction.interpreter,
    };
  }
  return null;
};

export const useStartJourney = ({ isLoggedIn = false, clientId = null } = {}) => {
  const restoredRef = useRef(false);
  const [hydrated, setHydrated] = useState(false);
  const [stage, setStage] = useState("welcome");
  const [source, setSource] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [preview, setPreview] = useState(null);
  const [extraction, setExtraction] = useState(null);
  const [interpreter, setInterpreter] = useState(null);
  const [schoolName, setSchoolName] = useState("");
  const [schoolAddress, setSchoolAddress] = useState("");
  const [schoolDecision, setSchoolDecision] = useState("");
  const [maxPassengers, setMaxPassengers] = useState(DEFAULT_MAX_PASSENGERS);
  const [preferBundled, setPreferBundled] = useState(false);
  const [operation, setOperation] = useState(null);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [walkthroughRole, setWalkthroughRole] = useState("dispatcher");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [commitError, setCommitError] = useState(null);
  const [existingStudentCount, setExistingStudentCount] = useState(null);
  const [intent, setIntentState] = useState(null);
  const [intentTouched, setIntentTouched] = useState(false);
  const [existingMode, setExistingMode] = useState(EXISTING_MODES.SKIP);
  const [existingDecisions, setExistingDecisions] = useState({});
  const [includeRouteList, setIncludeRouteList] = useState(false);
  const [serviceDirection, setServiceDirectionState] = useState(null);
  const [serviceDirectionTouched, setServiceDirectionTouched] = useState(false);
  const [match, setMatch] = useState(null);
  const [matchBusy, setMatchBusy] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const [loadSeq, setLoadSeq] = useState(0);
  // Saving runs as a server job; see "Saving runs as a server job" below.
  const [jobId, setJobId] = useState(null);
  const [commitSession, setCommitSession] = useState(null);
  const [jobProgress, setJobProgress] = useState(null);
  const [pendingJob, setPendingJob] = useState(null);
  // Workspace session for a roster already written, kept only so a failed login can be retried
  // without opening a second workspace.
  const [handoffSession, setHandoffSession] = useState(null);
  const processGenRef = useRef(0);
  const matchAbortRef = useRef(null);
  const jobAbortRef = useRef(null);

  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    const restored = restoreJourney();
    if (!restored) {
      // A roster written just before this mount (the visitor's login remounts the tree): the file
      // itself is gone from this browser, only the result is handed over so the Continue stage can
      // show the summary and open the right screen.
      const finished = peekImportFinished();
      const ownerId = finished?.session?.id ? String(finished.session.id) : null;
      // A marker belongs to the workspace it was written in. Signed in as someone else, it must
      // not take over their screen; it stays until that workspace signs in or it expires.
      const markerFits = finished?.result && (!isLoggedIn || !ownerId || String(clientId || "") === ownerId);
      if (markerFits) {
        setSource(finished.source || "upload");
        setFileName(finished.fileName || null);
        setIntentState(finished.intent || finished.result.intent || null);
        setIntentTouched(true);
        setCommitResult(finished.result);
        setHandoffSession(finished.session?.token ? finished.session : null);
        setStage("continue");
      }
      setHydrated(true);
      return;
    }
    setStage(restored.stage);
    setSource(restored.source);
    setFileName(restored.fileName);
    setParsed(restored.parsed);
    setMapping(restored.mapping);
    setPreview(restored.preview);
    setExtraction(restored.extraction);
    setInterpreter(restored.interpreter);
    setSchoolName(restored.schoolName);
    setSchoolAddress(restored.schoolAddress || "");
    setSchoolDecision(restored.schoolDecision || "");
    setMaxPassengers(restored.maxPassengers);
    setPreferBundled(restored.preferBundled);
    setSelectedRouteId(restored.selectedRouteId);
    setWalkthroughRole(restored.walkthroughRole);
    setIntentState(restored.intent);
    setIntentTouched(restored.intentTouched);
    setExistingMode(restored.existingMode);
    setExistingDecisions(restored.existingDecisions);
    setIncludeRouteList(restored.includeRouteList);
    setServiceDirectionState(restored.serviceDirection);
    setServiceDirectionTouched(restored.serviceDirectionTouched);
    setCommitResult(restored.commitResult);
    setCommitError(restored.commitError);
    // A workspace already opened, or a job still running: resume that, never open a second one.
    if (!restored.commitResult && (restored.commitJobId || restored.commitSession?.token)) {
      setJobId(restored.commitJobId || null);
      setCommitSession(restored.commitSession);
      setPendingJob({ jobId: restored.commitJobId || null, session: restored.commitSession });
    }
    setHydrated(true);
  }, []);

  // Both an uploaded file and the sample are rosters that get written; the sample is rebuilt on
  // restore instead of being stored.
  const isRoster = source === "upload" || source === "sample";

  useEffect(() => {
    if (!hydrated) return;
    // Once the roster is in the database nothing of it stays in this browser.
    if (commitResult) {
      clearStartJourney();
      return;
    }
    persistStartJourney({
      stage,
      source,
      fileName,
      parsed: source === "upload" ? parsed : null,
      mapping: source === "upload" ? mapping : null,
      preview: source === "upload" ? preview : null,
      extraction: source === "upload" ? extraction : null,
      interpreter: source === "upload" ? interpreter : null,
      schoolName,
      schoolAddress,
      schoolDecision,
      maxPassengers,
      preferBundled,
      selectedRouteId,
      walkthroughRole,
      intent,
      intentTouched,
      existingMode,
      existingDecisions,
      includeRouteList,
      serviceDirection,
      serviceDirectionTouched,
      commitResult: isRoster ? commitResult : null,
      commitError: isRoster ? commitError : null,
      commitJobId: isRoster ? jobId : null,
      commitSession: isRoster ? commitSession : null,
    });
  }, [
    hydrated,
    stage,
    source,
    fileName,
    parsed,
    mapping,
    preview,
    extraction,
    interpreter,
    schoolName,
    schoolAddress,
    schoolDecision,
    maxPassengers,
    preferBundled,
    selectedRouteId,
    walkthroughRole,
    intent,
    intentTouched,
    existingMode,
    existingDecisions,
    includeRouteList,
    serviceDirection,
    serviceDirectionTouched,
    commitResult,
    commitError,
    jobId,
    commitSession,
    isRoster,
  ]);

  // ---- service direction: morning / afternoon / both, proposed from the file until the operator picks ----
  const directionSuggestion = useMemo(() => {
    if (source !== "upload" || !extraction) return null;
    return suggestServiceDirection(extraction);
  }, [source, extraction]);

  useEffect(() => {
    if (!directionSuggestion) return;
    if (serviceDirectionTouched && serviceDirection) return;
    if (directionSuggestion.suggested !== serviceDirection) setServiceDirectionState(directionSuggestion.suggested);
  }, [directionSuggestion, serviceDirection, serviceDirectionTouched]);

  const setServiceDirection = useCallback((next) => {
    setServiceDirectionState(next);
    setServiceDirectionTouched(true);
  }, []);

  // ---- intent: suggested from the file (and from what is already registered) until the operator picks ----
  const intentSuggestion = useMemo(() => {
    if (source !== "upload" || !extraction) return null;
    return suggestIntent(extraction, match?.summary);
  }, [source, extraction, match]);

  useEffect(() => {
    if (!intentSuggestion) return;
    if (intentTouched && intent && intentSuggestion.options.includes(intent)) return;
    if (intentSuggestion.suggested !== intent) setIntentState(intentSuggestion.suggested);
  }, [intentSuggestion, intent, intentTouched]);

  const setIntent = useCallback((next) => {
    setIntentState(next);
    setIntentTouched(true);
  }, []);

  const stages = useMemo(() => stagesForJourney({ source, intent }), [source, intent]);

  const goTo = useCallback(
    (next) => {
      if (!stages.includes(next)) return;
      setStage(next);
    },
    [stages],
  );

  const goNext = useCallback(() => {
    const index = stages.indexOf(stage);
    if (index >= 0 && index < stages.length - 1) setStage(stages[index + 1]);
  }, [stage, stages]);

  const goBack = useCallback(() => {
    const index = stages.indexOf(stage);
    if (index > 0) setStage(stages[index - 1]);
  }, [stage, stages]);

  useEffect(() => {
    if (stage !== "welcome" && !stages.includes(stage)) setStage(stages[stages.length - 1]);
  }, [stage, stages]);

  // ---- processing overlay ----
  const playProcessing = useCallback(async (script, work) => {
    const generation = ++processGenRef.current;
    const started = Date.now();
    setProcessing({ script });
    try {
      const result = work ? await work() : undefined;
      if (!shouldSkipProcessingDelay()) {
        const remaining = MIN_PROCESS_MS - (Date.now() - started);
        if (remaining > 0) await wait(remaining);
      }
      return { generation, result };
    } catch (err) {
      if (processGenRef.current === generation) {
        setProcessing(null);
        setBusy(false);
      }
      throw err;
    }
  }, []);

  const applyParsed = useCallback((nextParsed, nextSource, nextFileName, nextOperation, nextExtraction, nextInterpreter, nextMatch) => {
    const nextMapping = nextParsed.mapping;
    const resolvedExtraction =
      nextSource === "upload"
        ? nextExtraction || extractionFromParsedTable(nextParsed)
        : nextSource === "sample"
          ? nextExtraction || null
          : null;
    const nextPreview = resolvedExtraction
      ? extractionToPreview(resolvedExtraction)
      : nextParsed.preview || buildPreviewFromMapping(nextParsed.rows, nextMapping, nextParsed.headers);
    setParsed(nextParsed);
    setMapping(nextMapping);
    setPreview(nextPreview);
    setExtraction(resolvedExtraction);
    setInterpreter(nextInterpreter || resolvedExtraction?.interpreter || nextSource);
    setSource(nextSource);
    setFileName(nextFileName);
    setOperation(nextOperation || null);
    setSelectedRouteId(nextOperation?.routes?.[0]?.id || null);
    setCommitResult(null);
    setCommitError(null);
    setExistingStudentCount(null);
    setMatch(nextMatch || null);
    setLoadSeq((current) => current + 1);
    setIntentState(null);
    setIntentTouched(false);
    setExistingMode(EXISTING_MODES.SKIP);
    setExistingDecisions({});
    setIncludeRouteList(false);
    setServiceDirectionState(null);
    setServiceDirectionTouched(false);
    setUndoStack([]);
    const found = (resolvedExtraction?.schools || []).filter((school) => school.name);
    if ((nextSource === "upload" || nextSource === "sample") && found.length) {
      setSchoolDecision("found");
      setSchoolName(found[0].name);
      setSchoolAddress(found[0].address || "");
    } else if (nextSource === "upload") {
      setSchoolDecision("");
      setSchoolName("");
      setSchoolAddress("");
    }
    setError(null);
    setStage("understand");
  }, []);

  const finishProcessing = useCallback((generation) => {
    if (processGenRef.current !== generation) return;
    setProcessing(null);
    setBusy(false);
  }, []);

  // The sample uses the same save job as an upload, without the questions. The roster is fixed,
  // so the server writes it in one pass and the screen does not stream rows. A customer's own
  // file still saves row by row.
  const abandonOpenImport = useCallback(() => {
    jobAbortRef.current?.abort();
    setPendingJob(null);
    setJobId(null);
    setCommitSession(null);
    setCommitError(null);
    persistStartJourney({
      ...readStartJourney(),
      commitJobId: null,
      commitSession: null,
      commitError: null,
    });
  }, []);

  const loadSample = useCallback(async () => {
    abandonOpenImport();
    setBusy(true);
    setError(null);
    const nextParsed = getSampleParsedSpreadsheet();
    const nextExtraction = normalizeExtraction(getSampleExtraction(), { interpreter: "sample" });
    const { generation } = await playProcessing(sampleProcessScript());
    if (processGenRef.current !== generation) return;
    applyParsed(nextParsed, "sample", SAMPLE_FILE_NAME, null, nextExtraction, "sample");
    setIntentState(INTENTS.PASSENGERS_ROUTES);
    setIntentTouched(true);
    setStage("continue");
    finishProcessing(generation);
  }, [abandonOpenImport, applyParsed, finishProcessing, playProcessing]);

  const loadFile = useCallback(
    async (file) => {
      abandonOpenImport();
      setBusy(true);
      setError(null);
      try {
        const { generation, result } = await playProcessing(uploadProcessScript(file.name), async () => {
          logStart("upload begin", { name: file.name, bytes: file.size });
          const controller = new AbortController();
          const analyzeStarted = Date.now();
          const analyzePromise = analyzeSpreadsheet(file, { signal: controller.signal })
            .then((analyzed) => {
              logStart("analyze ok", {
                ms: Date.now() - analyzeStarted,
                interpreter: analyzed?.interpreter,
                passengers: analyzed?.extraction?.stats?.passengerCount,
                routeList: analyzed?.extraction?.stats?.routeListCount,
                existing: analyzed?.match?.summary?.existing,
              });
              return analyzed;
            })
            .catch((err) => {
              logStart("analyze skip", { ms: Date.now() - analyzeStarted, code: err?.code, message: err?.message });
              return null;
            });

          const parseStarted = Date.now();
          const nextParsed = await parseSpreadsheetFile(file);
          let nextExtraction = extractionFromParsedTable(nextParsed);
          let nextInterpreter = "heuristic";
          logStart("local parse", {
            ms: Date.now() - parseStarted,
            sheets: nextParsed.sheetNames,
            rows: nextParsed.rows?.length,
            passengers: nextExtraction.stats?.passengerCount,
            routeList: nextExtraction.stats?.routeListCount,
          });

          const waitMs = isTest() ? 0 : nextExtraction.stats?.passengerCount ? ANALYZE_WAIT_LOCAL_MS : ANALYZE_WAIT_EMPTY_MS;
          let settled = false;
          const analyzed = await Promise.race([
            analyzePromise.then((value) => {
              settled = true;
              return value;
            }),
            wait(waitMs).then(() => {
              if (!settled) {
                logStart("analyze wait cap", { waitMs });
                controller.abort();
              }
              return null;
            }),
          ]);

          if (analyzed?.extraction?.stats?.passengerCount || analyzed?.extraction?.stats?.routeListCount) {
            nextExtraction = normalizeExtraction(analyzed.extraction, {
              interpreter: analyzed.interpreter || analyzed.extraction.interpreter,
              rowCount: analyzed.table?.rowCount || nextParsed.rows.length,
              schoolsFromWorkbook: analyzed.table?.schoolsFromWorkbook || analyzed.extraction.schools,
              routeList: analyzed.extraction.routes || analyzed.table?.routeList || nextParsed.routeList || [],
            });
            nextInterpreter = analyzed.interpreter || nextExtraction.interpreter;
            if (analyzed.table?.sheetNames) {
              nextParsed.sheetNames = analyzed.table.sheetNames;
              nextParsed.sheetName = analyzed.table.sheetName || nextParsed.sheetName;
              nextParsed.schoolsFromWorkbook = analyzed.table.schoolsFromWorkbook || nextParsed.schoolsFromWorkbook;
              nextParsed.routeList = analyzed.table.routeList || nextParsed.routeList;
            }
          } else if (analyzed?.extraction) {
            nextExtraction = {
              ...nextExtraction,
              warnings: [...(nextExtraction.warnings || []), ...(analyzed.extraction.warnings || [])].filter(
                (warning) => warning && !/interpreter was unavailable|read locally|took too long|analyze_timeout/i.test(warning),
              ),
            };
          }
          logStart("overlay ready", { interpreter: nextInterpreter, passengers: nextExtraction.stats?.passengerCount });
          return { nextParsed, nextExtraction, nextInterpreter, nextMatch: analyzed?.match || null };
        });
        if (processGenRef.current !== generation) return;
        if (!result) {
          finishProcessing(generation);
          return;
        }
        applyParsed(
          { ...result.nextParsed, preview: extractionToPreview(result.nextExtraction) },
          "upload",
          file.name,
          null,
          result.nextExtraction,
          result.nextInterpreter,
          result.nextMatch,
        );
        finishProcessing(generation);
      } catch (err) {
        processGenRef.current += 1;
        setProcessing(null);
        setBusy(false);
        setError(err?.message || "The spreadsheet could not be read.");
      }
    },
    [abandonOpenImport, applyParsed, finishProcessing, playProcessing],
  );

  // ---- matching against what the company already has (logged-in only) ----
  const matchKey = useMemo(() => {
    if (source !== "upload" || !extraction) return "";
    return (extraction.passengers || [])
      .filter((passenger) => passenger.name)
      .map((passenger) => `${passenger.rowNumber}|${passenger.name}|${passenger.schoolName}|${passenger.identifier}`)
      .join("\n");
  }, [source, extraction]);

  // Re-runs on every edit of names/schools and on every new file, even when the rows are identical
  // to the previous upload (loadSeq), because applyParsed clears the previous match.
  useEffect(() => {
    if (!isLoggedIn || !matchKey || isTest()) return undefined;
    const timer = setTimeout(async () => {
      if (matchAbortRef.current) matchAbortRef.current.abort();
      const controller = new AbortController();
      matchAbortRef.current = controller;
      setMatchBusy(true);
      try {
        const result = await matchPassengers(extraction?.passengers || [], { signal: controller.signal });
        if (!controller.signal.aborted) setMatch(result);
      } catch (err) {
        if (err?.name !== "AbortError") logStart("match skip", { message: err?.message });
      } finally {
        if (matchAbortRef.current === controller) setMatchBusy(false);
      }
    }, MATCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchKey, isLoggedIn, loadSeq]);

  // ---- operator edits on the passenger list ----
  const updatePassenger = useCallback((rowNumber, patch) => {
    setExtraction((current) => (current ? patchPassenger(current, rowNumber, patch) : current));
  }, []);

  const removeRows = useCallback(
    (rowNumbers, label) => {
      const list = Array.isArray(rowNumbers) ? rowNumbers : [rowNumbers];
      if (!list.length || !extraction) return;
      const { extraction: next, removed } = removePassengers(extraction, list);
      if (!removed.length) return;
      setExtraction(next);
      setUndoStack((stack) => [...stack.slice(-9), { removed, label: label || null }]);
      setExistingDecisions((current) => {
        const nextDecisions = { ...current };
        list.forEach((rowNumber) => delete nextDecisions[String(rowNumber)]);
        return nextDecisions;
      });
    },
    [extraction],
  );

  const removePassenger = useCallback((rowNumber) => removeRows([rowNumber]), [removeRows]);

  const undoRemove = useCallback(() => {
    if (!undoStack.length || !extraction) return;
    const last = undoStack[undoStack.length - 1];
    setExtraction(restorePassengers(extraction, last.removed));
    setUndoStack(undoStack.slice(0, -1));
  }, [undoStack, extraction]);

  const setExistingDecision = useCallback((rowNumber, action) => {
    setExistingDecisions((current) => {
      const next = { ...current };
      if (!action) delete next[String(rowNumber)];
      else next[String(rowNumber)] = { action };
      return next;
    });
  }, []);

  const applyManualMapping = useCallback(
    (nextMapping) => {
      if (!parsed) return;
      setMapping(nextMapping);
      const next = extractionFromMapping(parsed, nextMapping);
      setExtraction(next);
      setInterpreter("manual");
      setUndoStack([]);
    },
    [parsed],
  );

  const resetJourney = useCallback(() => {
    setStage("welcome");
    setSource(null);
    setFileName(null);
    setParsed(null);
    setMapping(null);
    setPreview(null);
    setExtraction(null);
    setInterpreter(null);
    setSchoolName("");
    setSchoolAddress("");
    setSchoolDecision("");
    setMaxPassengers(DEFAULT_MAX_PASSENGERS);
    setPreferBundled(false);
    setOperation(null);
    setSelectedRouteId(null);
    setWalkthroughRole("dispatcher");
    setError(null);
    setBusy(false);
    setProcessing(null);
    setCommitResult(null);
    setCommitError(null);
    setExistingStudentCount(null);
    setIntentState(null);
    setIntentTouched(false);
    setExistingMode(EXISTING_MODES.SKIP);
    setExistingDecisions({});
    setIncludeRouteList(false);
    setServiceDirectionState(null);
    setServiceDirectionTouched(false);
    setMatch(null);
    setUndoStack([]);
    setJobId(null);
    setCommitSession(null);
    setJobProgress(null);
    setPendingJob(null);
    setHandoffSession(null);
    processGenRef.current += 1;
  }, []);

  // ---- derived state ----
  const passengerCount = extraction?.stats?.passengerCount || 0;
  const routeListCount = extraction?.stats?.routeListCount || 0;
  const schoolMissing = Boolean(source === "upload" && extraction && passengerCount > 0 && extraction.stats.missing?.school === passengerCount);
  const schoolChosen =
    schoolDecision === "later" ||
    ((schoolDecision === "create" || schoolDecision === "found" || schoolDecision === "existing") && Boolean(String(schoolName).trim())) ||
    (extraction?.stats?.schoolCount > 0 && Boolean(String(schoolName).trim()));

  const routesOnlyNeedsSchool = intent === INTENTS.ROUTES_ONLY && passengerCount === 0 && routeListCount > 0 && !String(schoolName).trim();

  const canAdvanceFromUnderstand =
    source === "upload"
      ? Boolean(intent) &&
        (intent === INTENTS.ROUTES_ONLY ? passengerCount > 0 || routeListCount > 0 : passengerCount > 0) &&
        (!schoolMissing || schoolChosen || intent === INTENTS.ROUTES_ONLY) &&
        !routesOnlyNeedsSchool
      : mappingIsUsable(mapping);

  const schoolRequired = Boolean(schoolMissing && schoolDecision !== "later" && intent !== INTENTS.ROUTES_ONLY);

  const canAdvanceFromPrepare = source !== "upload" || !schoolRequired || Boolean(String(schoolName).trim()) || schoolDecision === "later";

  const workingExtraction = useMemo(() => {
    if (source !== "upload" || !extraction) return extraction;
    return applyOperatorSchool(extraction, { schoolName, schoolAddress, schoolDecision });
  }, [source, extraction, schoolName, schoolAddress, schoolDecision]);

  useEffect(() => {
    if (source !== "upload" || !workingExtraction) return;
    setPreview(extractionToPreview(workingExtraction));
  }, [source, workingExtraction]);

  const servicePlan = useMemo(() => {
    if (source !== "upload" || !workingExtraction?.passengers) return { pickup: [], dropoff: [] };
    // "Later" mirrors the backend: students land under the placeholder school and auto groups are
    // called "Service 1" rather than borrowing a name that is about to change.
    const later = schoolDecision === "later";
    const fallback = later
      ? PLACEHOLDER_SCHOOL_NAME
      : String(schoolName || fileName || "Imported school").replace(/\.[^.]+$/, "") || "Imported school";
    return draftServicePlan(
      workingExtraction.passengers,
      maxPassengers,
      fallback,
      serviceDirection || SERVICE_DIRECTIONS.BOTH,
      workingExtraction.routes || [],
      later ? { autoLabel: GENERIC_SERVICE_LABEL } : {},
    );
  }, [source, workingExtraction, maxPassengers, schoolName, fileName, serviceDirection, schoolDecision]);

  const draftGroups = useMemo(() => [...servicePlan.pickup, ...servicePlan.dropoff], [servicePlan]);

  const commitPayload = useCallback(
    (confirmExistingOperation) => ({
      extraction: applyOperatorSchool(extraction, { schoolName, schoolAddress, schoolDecision }),
      schoolName: String(schoolName || "").trim(),
      schoolAddress: String(schoolAddress || "").trim(),
      schoolDecision,
      fileName,
      intent,
      existingMode,
      existingDecisions,
      includeRouteList,
      serviceDirection: serviceDirection || SERVICE_DIRECTIONS.BOTH,
      maxPassengersPerVehicle: maxPassengers,
      preferBundled,
      confirmExistingOperation,
    }),
    [
      extraction,
      schoolName,
      schoolAddress,
      schoolDecision,
      fileName,
      intent,
      existingMode,
      existingDecisions,
      includeRouteList,
      serviceDirection,
      maxPassengers,
      preferBundled,
    ],
  );

  // ---- Saving runs as a server job -------------------------------------------------------
  // The commit answers with a jobId at once; the journey watches it and shows real progress.
  // jobId (and, for a visitor, the workspace session) are persisted so a refresh, a closed tab
  // or the login remount picks the same job up instead of losing the result.
  const rememberJob = useCallback((nextJobId, session) => {
    setJobId(nextJobId);
    setCommitSession(session ? { id: session.id, token: session.token } : null);
    persistStartJourney({
      ...readStartJourney(),
      stage: "continue",
      commitJobId: nextJobId || null,
      commitSession: session ? { id: session.id, token: session.token } : null,
      commitResult: null,
      commitError: null,
    });
  }, []);

  const forgetJob = useCallback(() => {
    setJobId(null);
    setCommitSession(null);
    persistStartJourney({ ...readStartJourney(), commitJobId: null, commitSession: null });
  }, []);

  const watchJob = useCallback(async (id, { token } = {}) => {
    jobAbortRef.current?.abort();
    const controller = new AbortController();
    jobAbortRef.current = controller;
    setJobProgress((prev) => (prev && prev.jobId === id ? prev : { ...EMPTY_PROGRESS, jobId: id }));
    try {
      return await waitForCommitJob(id, {
        token,
        signal: controller.signal,
        onProgress: (job) => setJobProgress((prev) => mergeJobProgress(prev, job, id)),
      });
    } finally {
      if (jobAbortRef.current === controller) jobAbortRef.current = null;
    }
  }, []);

  useEffect(() => () => jobAbortRef.current?.abort(), []);

  const commitOperation = useCallback(
    async ({ confirmExistingOperation = true } = {}) => {
      if (!extraction) {
        const err = new Error("There is no interpreted roster to import.");
        setCommitError(err.message);
        throw err;
      }
      setBusy(true);
      setCommitError(null);
      setJobProgress({ ...EMPTY_PROGRESS });
      try {
        const started = await withDeadline(
          startCommitJob(commitPayload(confirmExistingOperation)),
          WORKSPACE_TIMEOUT_MS,
          "The server did not answer. Check your connection and try again.",
        );
        rememberJob(started.jobId, null);
        setJobProgress((prev) => ({ ...(prev || EMPTY_PROGRESS), jobId: started.jobId, total: started.total || prev?.total || 0 }));
        const result = await watchJob(started.jobId);
        setCommitResult(result);
        setExistingStudentCount(null);
        setJobId(null);
        setCommitSession(null);
        // Written to the database: the local copy of the file goes now, not "eventually".
        clearStartJourney();
        return result;
      } catch (err) {
        if (err?.name === "AbortError") throw err;
        if (err.code === "CONFIRM_EXISTING") {
          setExistingStudentCount(err.payload?.existingStudentCount ?? null);
        }
        setCommitError(describeCommitError(err));
        forgetJob();
        throw err;
      } finally {
        setBusy(false);
        setJobProgress(null);
      }
    },
    [extraction, commitPayload, rememberJob, forgetJob, watchJob],
  );

  // Visitor path: open an empty workspace, save the roster into it with that session's token,
  // *then* switch the app to that session. Logging in remounts the whole tree (the demo
  // provider wraps it), so the result is persisted first and picked up by the remounted journey.
  const [workspaceStep, setWorkspaceStep] = useState(null);
  const createWorkspaceAndCommit = useCallback(
    async ({ sendRequest, login, search } = {}) => {
      if (!extraction) {
        const err = new Error("There is no interpreted roster to import.");
        setCommitError(err.message);
        throw err;
      }
      setBusy(true);
      setCommitError(null);
      setWorkspaceStep("creating");
      let session = null;
      let aborted = false;
      let succeeded = false;
      try {
        ({ session } = await withDeadline(
          createDemoSession({ sendRequest, search, seed: false, source: "start-import" }),
          WORKSPACE_TIMEOUT_MS,
          "The server did not answer while opening your workspace. Check your connection and try again.",
        ));
        // Remember the workspace before the import starts, so a refresh resumes it instead of
        // opening a second one.
        rememberJob(null, session);
        setWorkspaceStep("saving");
        setJobProgress({ ...EMPTY_PROGRESS });
        const started = await withDeadline(
          startCommitJob(commitPayload(true), { token: session.token }),
          WORKSPACE_TIMEOUT_MS,
          "The server did not answer while starting the import. Your workspace is open — log in and try saving again.",
        );
        rememberJob(started.jobId, session);
        setJobProgress((prev) => ({ ...(prev || EMPTY_PROGRESS), jobId: started.jobId, total: started.total || prev?.total || 0 }));
        const result = await watchJob(started.jobId, { token: session.token });
        setCommitResult(result);
        setExistingStudentCount(null);
        setJobId(null);
        setCommitSession(null);
        // Written: the file leaves this browser; the login below remounts the tree, which picks
        // the result up from the one-shot marker.
        clearStartJourney();
        markImportFinished({ result, source, intent, fileName, session });
        succeeded = true;
        return result;
      } catch (err) {
        if (err?.name === "AbortError") {
          // This page went away while the job runs; the persisted jobId lets the next mount resume it.
          aborted = true;
          throw err;
        }
        const message = describeCommitError(err);
        setCommitError(message);
        setJobId(null);
        setCommitSession(null);
        if (session) {
          // The workspace exists even though saving failed; keep the error so the user can retry once logged in.
          persistStartJourney({
            ...readStartJourney(),
            stage: "continue",
            commitJobId: null,
            commitSession: null,
            commitResult: null,
            commitError: message,
          });
        }
        throw err;
      } finally {
        setWorkspaceStep(null);
        setBusy(false);
        setJobProgress(null);
        if (!aborted && succeeded && session && login) {
          markEnterStartExperience();
          await login(session.id, session.token);
        }
      }
    },
    [extraction, commitPayload, rememberJob, watchJob, source, intent, fileName],
  );

  // A job left running by a refresh, a closed tab, or the login remount. The caller (Continue
  // stage) resumes it once, with the app's login for the session hand-off when it was a visitor.
  const resumeCommit = useCallback(
    async ({ login } = {}) => {
      const pending = pendingJob;
      if (!pending) return null;
      setPendingJob(null);
      setBusy(true);
      setCommitError(null);
      const session = pending.session || null;
      if (session) setWorkspaceStep("saving");
      let aborted = false;
      let succeeded = false;
      try {
        let activeJobId = pending.jobId;
        if (!activeJobId) {
          if (!session?.token || !extraction) return null;
          const started = await startCommitJob(commitPayload(true), { token: session.token });
          rememberJob(started.jobId, session);
          activeJobId = started.jobId;
        }
        const result = await watchJob(activeJobId, { token: session?.token });
        setCommitResult(result);
        setExistingStudentCount(null);
        setJobId(null);
        setCommitSession(null);
        clearStartJourney();
        if (session) markImportFinished({ result, source, intent, fileName, session });
        succeeded = true;
        return result;
      } catch (err) {
        if (err?.name === "AbortError") {
          aborted = true;
          return null;
        }
        const message = describeCommitError(err);
        setJobId(null);
        setCommitSession(null);
        // A visitor sample whose job vanished (server restart, the in-memory job is gone) should
        // open a fresh workspace. Keeping the error blocks that automatic start.
        const retrySample = err?.code === "JOB_LOST" && source === "sample" && !isLoggedIn;
        setCommitError(retrySample ? null : message);
        persistStartJourney({
          ...readStartJourney(),
          stage: "continue",
          commitJobId: null,
          commitSession: null,
          commitResult: null,
          commitError: retrySample ? null : message,
        });
        return null;
      } finally {
        setWorkspaceStep(null);
        setBusy(false);
        setJobProgress(null);
        if (!aborted && succeeded && session && login) {
          markEnterStartExperience();
          await login(session.id, session.token);
        }
      }
    },
    [pendingJob, watchJob, source, intent, fileName, extraction, commitPayload, rememberJob, isLoggedIn],
  );

  const selectedRoute = useMemo(() => {
    if (!operation?.routes?.length) return null;
    return operation.routes.find((route) => route.id === selectedRouteId) || operation.routes[0];
  }, [operation, selectedRouteId]);

  return {
    stage,
    stages,
    source,
    fileName,
    parsed,
    mapping,
    preview,
    extraction,
    workingExtraction,
    interpreter,
    schoolName,
    schoolAddress,
    schoolDecision,
    maxPassengers,
    preferBundled,
    draftGroups,
    servicePlan,
    serviceDirection,
    directionSuggestion,
    operation,
    selectedRouteId,
    selectedRoute,
    walkthroughRole,
    error,
    busy,
    processing,
    commitResult,
    commitError,
    existingStudentCount,
    schoolRequired,
    schoolMissing,
    canAdvanceFromUnderstand,
    canAdvanceFromPrepare,
    intent,
    intentSuggestion,
    intentTouched,
    createsRoutes: intentCreatesRoutes(intent),
    existingMode,
    existingDecisions,
    includeRouteList,
    match,
    matchBusy,
    undoStack,
    goTo,
    goNext,
    goBack,
    loadSample,
    loadFile,
    applyManualMapping,
    updatePassenger,
    removePassenger,
    removeRows,
    undoRemove,
    setIntent,
    setExistingMode,
    setExistingDecision,
    setIncludeRouteList,
    setServiceDirection,
    setSelectedRouteId,
    setWalkthroughRole,
    setSchoolName,
    setSchoolAddress,
    setSchoolDecision,
    setMaxPassengers,
    setPreferBundled,
    commitOperation,
    createWorkspaceAndCommit,
    resumeCommit,
    pendingJob,
    handoffSession,
    jobProgress,
    workspaceStep,
    resetJourney,
  };
};
